import ReactGA from "react-ga4";

export const GA_MEASUREMENT_ID = "G-WS8BTZMD3D";
export const GA_STREAM_ID = "14864231880";
export const COOKIE_CONSENT_STORAGE_KEY = "cyber_web_cookie_consent";

export type AnalyticsConsent = "granted" | "denied";

export type StoredCookieConsent = {
    necessary: true;
    analytics: boolean;
    updatedAt: string;
    version: 1;
};

declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: (...args: unknown[]) => void;
    }
}

let initialized = false;

const hasWindow = () => typeof window !== "undefined";

export const normalizeConsent = (raw: string | null): StoredCookieConsent | null => {
    if (!raw) return null;

    if (raw === "all" || raw === "necessary") {
        return {
            necessary: true,
            analytics: raw === "all",
            updatedAt: new Date().toISOString(),
            version: 1,
        };
    }

    try {
        const parsed = JSON.parse(raw) as Partial<StoredCookieConsent>;
        if (parsed.necessary === true && typeof parsed.analytics === "boolean") {
            return {
                necessary: true,
                analytics: parsed.analytics,
                updatedAt: typeof parsed.updatedAt === "string" ? parsed.updatedAt : new Date().toISOString(),
                version: 1,
            };
        }
    } catch {
        return null;
    }

    return null;
};

export const getStoredCookieConsent = (): StoredCookieConsent | null => {
    if (!hasWindow()) return null;
    return normalizeConsent(window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY));
};

export const saveCookieConsent = (analytics: boolean): StoredCookieConsent => {
    const consent: StoredCookieConsent = {
        necessary: true,
        analytics,
        updatedAt: new Date().toISOString(),
        version: 1,
    };

    window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(consent));
    if (!analytics) {
        clearAnalyticsCookies();
    }
    window.dispatchEvent(new CustomEvent("cyber-cookie-consent-change", { detail: consent }));
    return consent;
};

export const clearAnalyticsCookies = () => {
    if (!hasWindow()) return;

    const hostParts = window.location.hostname.split(".");
    const domains = hostParts.map((_, index) => `.${hostParts.slice(index).join(".")}`);
    domains.push(window.location.hostname);

    document.cookie.split(";").forEach((cookie) => {
        const cookieName = cookie.split("=")[0]?.trim();
        if (!cookieName || !/^(_ga|_gid|_gat|_gac_|_gcl_)/.test(cookieName)) return;

        document.cookie = `${cookieName}=; Max-Age=0; path=/; SameSite=Lax`;
        domains.forEach((domain) => {
            document.cookie = `${cookieName}=; Max-Age=0; path=/; domain=${domain}; SameSite=Lax`;
        });
    });
};

export const getAnalyticsConsentState = (): AnalyticsConsent => {
    return getStoredCookieConsent()?.analytics ? "granted" : "denied";
};

export const setDefaultConsentMode = () => {
    if (!hasWindow()) return;

    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() {
        window.dataLayer?.push(arguments);
    };

    window.gtag("consent", "default", {
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        analytics_storage: getAnalyticsConsentState(),
        functionality_storage: "granted",
        security_storage: "granted",
        wait_for_update: 500,
    });
};

export const updateConsentMode = (analytics: boolean) => {
    if (!hasWindow()) return;

    const analyticsStorage = analytics ? "granted" : "denied";
    if (!analytics) {
        clearAnalyticsCookies();
    }

    window.gtag?.("consent", "update", {
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        analytics_storage: analyticsStorage,
        functionality_storage: "granted",
        security_storage: "granted",
    });
};

export const initializeAnalytics = () => {
    if (!hasWindow() || initialized || getAnalyticsConsentState() !== "granted") return;

    ReactGA.initialize(GA_MEASUREMENT_ID, {
        gtagOptions: {
            send_page_view: false,
            anonymize_ip: true,
        },
    });
    initialized = true;
    updateConsentMode(true);
};

export const hasAnalyticsConsent = () => getAnalyticsConsentState() === "granted";

export const trackEvent = (
    eventName: string,
    params: Record<string, string | number | boolean | undefined> = {}
) => {
    if (!hasAnalyticsConsent()) return;
    initializeAnalytics();
    ReactGA.event(eventName, {
        ...params,
        stream_id: GA_STREAM_ID,
    });
};

export const trackPageView = (path: string, title: string) => {
    if (!hasAnalyticsConsent()) return;
    initializeAnalytics();

    ReactGA.send({
        hitType: "pageview",
        page: path,
        title,
    });
};

export const trackTiming = (name: string, valueMs: number, label?: string) => {
    if (valueMs <= 0) return;

    trackEvent("time_engaged", {
        event_category: "engagement",
        name,
        value: Math.round(valueMs),
        event_label: label,
    });
};

export const isDownloadUrl = (href: string) => {
    return /\.(pdf|docx?|xlsx?|pptx?|zip|rar|7z|csv|txt|mp3|mp4|ics)(\?.*)?$/i.test(href);
};
